import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { chatSessionAtom } from '@localagent/sdk/react';
import { toast } from '@localagent/ui/components/sonner';
import { getSDK } from '../services/sdk-instance';
import {
  launchWorkspaceAction,
  dismissWorkspaceAction,
  launchedWorkspaceIdsAtom,
} from '../stores/workspace/atoms';

/**
 * The ONE attach-workspace-and-open-chat sequence (workspace-view-redesign
 * app-shell ADR-004). The hook launches the workspace and calls
 * `ChatSession.attachWorkspace`; persist and navigation belong to the SDK
 * plus the host callback.
 */
export function useAttachWorkspaceToChat() {
  const navigate = useNavigate();
  const live = useAtomValue(chatSessionAtom);
  const launchWorkspace = useSetAtom(launchWorkspaceAction);
  const dismissWorkspace = useSetAtom(dismissWorkspaceAction);
  const launchedWorkspaceIds = useAtomValue(launchedWorkspaceIdsAtom);
  const queryClient = useQueryClient();
  const [attaching, setAttaching] = useState(false);

  const attach = useCallback(
    (workspaceId: string) => {
      if (!workspaceId || attaching) return;
      setAttaching(true);
      const wasAlreadyLaunched = launchedWorkspaceIds.includes(workspaceId);
      launchWorkspace(workspaceId);

      const run = async () => {
        const sdk = getSDK();
        const session =
          live?.isDraft && !live.workspaceId ? live : await sdk.chat.startNewChat();
        await session.attachWorkspace(workspaceId);
        const persistedId = session.sessionId;
        if (!persistedId) {
          throw new Error('attachWorkspace did not persist a session id');
        }
        void queryClient.invalidateQueries({ queryKey: ['sessions', null], exact: true });
        void navigate(`/chat/${persistedId}`);
      };

      void run().catch((err: unknown) => {
        if (!wasAlreadyLaunched) {
          dismissWorkspace(workspaceId);
        }
        setAttaching(false);
        console.error('Failed to attach workspace to chat:', err);
        toast.error(err instanceof Error ? `Failed to attach workspace: ${err.message}` : 'Failed to attach workspace');
      });
    },
    [attaching, launchedWorkspaceIds, live, launchWorkspace, queryClient, navigate, dismissWorkspace],
  );

  return { attach, attaching };
}
